import removeAccents from 'remove-accents';
import {
  ExportPathPart,
  ExportPathSettings,
  ExportPartSettings,
  exportSettingsError,
} from 'components/tables/data/system-export/system-export-settings';

/** Windows naming rules: https://learn.microsoft.com/en-us/windows/win32/fileio/naming-a-file */
export interface ExportLimits {
  componentLength: number;
  pathLength: number;
}

export const DEFAULT_EXPORT_LIMITS: ExportLimits = { componentLength: 60, pathLength: 160 };
export const EXPORT_MAPPING_NAME = 'mapa-suborov.csv';

export interface ExportDirectory {
  /** Stable domain identity, scoped to its parent; names alone must not merge distinct objects. */
  id: string;
  name: string;
  prefix?: string;
  part?: ExportPathPart;
  displayId?: string;
}

export interface ExportEntry {
  fileId: string;
  directories: ExportDirectory[];
  fileName: string;
  namingId?: string;
}

/** A naming limit that the administrator can correct before any upload. */
export class ExportPathError extends Error {}

/** Validate bounded, per-export overrides independently of source discovery. */
export function exportLimitsError({ componentLength, pathLength }: ExportLimits): string | null {
  if (!Number.isInteger(componentLength) || componentLength < 20 || componentLength > 255) {
    return 'Dĺžka názvu musí byť celé číslo od 20 do 255.';
  }
  if (!Number.isInteger(pathLength) || pathLength < 40 || pathLength > 1024) {
    return 'Dĺžka cesty musí byť celé číslo od 40 do 1024.';
  }
  if (componentLength > pathLength) return 'Dĺžka názvu nesmie prekročiť dĺžku cesty.';
  return null;
}

/** Explain why protected path information cannot fit. */
const cannotFit = () =>
  new ExportPathError(
    'Zvolené limity sú príliš malé na zachovanie priečinkov, identifikátorov a prípon. Zvýšte limity v pokročilých nastaveniach.'
  );
/** Recognize Win32 device aliases, including names with extensions. */
const reserved = (name: string) => /^(con|prn|aux|nul|com[1-9¹²³]|lpt[1-9¹²³])$/i.test(name.split('.')[0].trimEnd());
/** Remove suffix characters ignored by the Windows shell. */
const trimEnd = (name: string) => name.replace(/[ .]+$/, '');
/** Normalize one name without interpreting user-supplied separators. */
const clean = (name: string) => {
  // The Unicode flag leaves valid surrogate pairs intact while replacing lone surrogates.
  const value =
    trimEnd(
      Array.from(removeAccents(name.normalize('NFD').replace(/\p{M}/gu, '').replace(/Ł/g, 'L')), (character) =>
        character.charCodeAt(0) < 32 ? '_' : character
      )
        .join('')
        .replace(/[<>:"/\\|?*\ud800-\udfff]/gu, '_')
    ) || '_';
  return reserved(value) ? `_${value}` : value;
};
/** Count disk filename bytes independently of Windows UTF-16 units. */
const utf8Length = (value: string) =>
  Array.from(value).reduce(
    (sum, character) => sum + encodeURIComponent(character).replace(/%[A-F\d]{2}/gi, '_').length,
    0
  );
/** Fit complete Unicode code points into both component budgets. */
const truncate = (value: string, units: number, bytes: number) => {
  let result = '';
  for (const character of value) {
    const size = utf8Length(character);
    if (result.length + character.length > units || size > bytes) break;
    result += character;
    bytes -= size;
  }
  return trimEnd(result) || '_';
};

interface PathNode {
  key: string;
  name: string;
  prefix: string;
  identifier: string;
  extension: string;
  budget: number;
  maximum: number;
  allocated: Map<string, PathNode>;
  leaves: Set<PathNode>;
  rendered: string;
  suffix: string;
  children: Map<string, PathNode>;
  parent?: PathNode;
}

/** Preserve the entity prefix and extension while fitting the descriptive label. */
function renderName(node: PathNode): string {
  const ending = node.suffix + node.extension;
  const units = node.budget - node.prefix.length - node.identifier.length - ending.length;
  const bytes = 255 - utf8Length(node.prefix + node.identifier + ending);
  if (units < (node.identifier ? 0 : 1) || bytes < (node.identifier ? 0 : 1)) throw cannotFit();
  const label = node.identifier
    ? node.name && units >= 4 && bytes >= 4
      ? ' - ' + truncate(node.name, units - 3, bytes - 3)
      : ''
    : truncate(node.name, units, bytes);
  let name = node.prefix + node.identifier + label + ending;
  // Truncation can itself produce a reserved name, e.g. CONifer -> CON.
  if (reserved(name)) {
    if (units < 2 || bytes < 2) throw cannotFit();
    name = '_' + node.prefix + truncate(node.name, units - 1, bytes - 1) + ending;
  }
  return name;
}

/** Minimum size preserves a selected identity, legacy prefix, collision suffix and extension. */
const minimumLength = (node: PathNode) =>
  node.prefix.length + node.identifier.length + node.extension.length + node.suffix.length + (node.identifier ? 0 : 1);

/** Update only the changed name; other sibling names remain stable. */
function allocateName(node: PathNode) {
  const allocated = node.parent!.allocated;
  if (node.rendered) allocated.delete(node.rendered.toUpperCase());
  let sequence = Number(node.suffix.slice(1)) || 1;
  node.rendered = renderName(node);
  while (allocated.has(node.rendered.toUpperCase())) {
    node.suffix = `~${++sequence}`;
    // New collisions may require space after a component was shortened to one character.
    node.budget = Math.max(node.budget, minimumLength(node));
    if (node.budget > node.maximum) throw cannotFit();
    node.rendered = renderName(node);
  }
  allocated.set(node.rendered.toUpperCase(), node);
}

/** Establish names once in stable order before adaptive shortening. */
function allocateChildren(parent: PathNode) {
  for (const node of Array.from(parent.children.values()).sort((a, b) =>
    a.key < b.key ? -1 : a.key > b.key ? 1 : 0
  )) {
    allocateName(node);
    allocateChildren(node);
  }
}

/** Return the shared nodes of a relative archive path. */
const pathNodes = (node: PathNode): PathNode[] => (node.parent ? [...pathNodes(node.parent), node] : []);
/** Read the currently allocated name of each shared directory. */
const pathName = (node: PathNode) =>
  pathNodes(node)
    .map((part) => part.rendered)
    .join('/');
/** Quote an original name and prevent spreadsheet formula execution. */
const csvCell = (value: string) =>
  `"${(/^[\s]*[=+@-]/.test(value) || /^[\t\r\n]/.test(value) ? "'" : '') + value.replace(/"/g, '""')}"`;

/** Allocate the complete manifest before uploads/batching, retaining shared folders and every source file. */
export function planSystemExport({
  entries,
  organizationName,
  organizationId,
  parts,
  date,
  limits,
}: {
  entries: ExportEntry[];
  organizationName: string;
  organizationId?: string;
  parts?: ExportPathSettings;
  date: string;
  limits: ExportLimits;
}) {
  const invalid = parts
    ? exportSettingsError({ parts, pathLength: limits.pathLength, content: {} })
    : exportLimitsError(limits);
  if (invalid) throw new ExportPathError(invalid);
  const root: PathNode = {
    key: '',
    name: '',
    prefix: '',
    identifier: '',
    extension: '',
    budget: 0,
    maximum: limits.componentLength,
    allocated: new Map(),
    leaves: new Set(),
    rendered: '',
    suffix: '',
    children: new Map(),
  };
  /** Reuse a folder by domain identity within its parent. */
  const add = (
    parent: PathNode,
    key: string,
    name: string,
    prefix = '',
    extension = '',
    settings?: ExportPartSettings,
    identifier = ''
  ) => {
    let node = parent.children.get(key);
    if (!node) {
      node = {
        key,
        name: settings?.style === 'id' ? '' : clean(name),
        prefix: settings ? '' : prefix,
        identifier: settings && settings.style !== 'label' ? clean(identifier) : '',
        extension,
        budget: settings ? Number(settings.maxLength) : limits.componentLength,
        maximum: settings ? Number(settings.maxLength) : limits.componentLength,
        allocated: new Map(),
        leaves: new Set(),
        rendered: '',
        suffix: '',
        children: new Map(),
        parent,
      };
      parent.children.set(key, node);
    }
    return node;
  };
  // Reserve the map name before allocating user-supplied root directories/files.
  const mapping = add(root, '0-map', 'mapa-suborov', '', '.csv');
  const seen = new Set<PathNode>();
  const references = entries
    .slice()
    .sort((a, b) => {
      const left = JSON.stringify(a),
        right = JSON.stringify(b);
      return left < right ? -1 : left > right ? 1 : 0;
    })
    .map((entry) => {
      let parent = root;
      for (const directory of entry.directories) {
        parent = add(
          parent,
          `2-directory:${directory.id}`,
          directory.name,
          directory.prefix,
          '',
          directory.part && parts ? parts[directory.part] : undefined,
          directory.displayId
        );
      }
      const safeName = clean(entry.fileName);
      const dot = safeName.lastIndexOf('.');
      const extension = dot > 0 ? safeName.slice(dot) : '';
      const name = dot > 0 ? safeName.slice(0, dot) : safeName;
      const node = add(
        parent,
        `1-file:${JSON.stringify([entry.fileName, entry.fileId])}`,
        name,
        '',
        extension,
        parts?.file,
        entry.namingId || entry.fileId
      );
      for (const part of pathNodes(node)) part.leaves.add(node);
      return { entry, node };
    })
    .filter(({ node }) => {
      if (seen.has(node)) return false;
      seen.add(node);
      return true;
    });
  allocateChildren(root);
  // Only an increased name can invalidate a previously fitted descendant. Queue those paths,
  // rather than re-rendering the entire archive for every device/file that needs shortening.
  const pending = references.map(({ node }) => node);
  const queued = new Set(pending);
  for (let index = 0; index < pending.length; index++) {
    const leaf = pending[index];
    queued.delete(leaf);
    while (pathName(leaf).length > limits.pathLength) {
      const excess = pathName(leaf).length - limits.pathLength;
      const candidates = pathNodes(leaf)
        .map((node) => ({
          node,
          minimum: minimumLength(node),
        }))
        .filter(({ node, minimum }) => node.rendered.length > minimum);
      candidates.sort((a, b) => b.node.rendered.length - a.node.rendered.length);
      const longest = candidates[0];
      if (!longest) throw cannotFit();
      const previousLength = longest.node.rendered.length;
      longest.node.budget = previousLength - Math.min(excess, previousLength - longest.minimum);
      allocateName(longest.node);
      if (longest.node.rendered.length > previousLength) {
        for (const descendant of longest.node.leaves) {
          if (!queued.has(descendant)) {
            queued.add(descendant);
            pending.push(descendant);
          }
        }
      }
    }
  }
  const archive: PathNode = {
    ...root,
    name: parts?.organization.style === 'id' ? '' : clean(organizationName),
    identifier: parts && parts.organization.style !== 'label' ? clean(organizationId || '') : '',
    budget: parts ? Number(parts.organization.maxLength) : limits.componentLength,
    extension: ` (${date}).zip`,
  };
  const archiveName = renderName(archive).slice(0, -4);
  const files = references.map(({ entry, node }) => ({ fileId: entry.fileId, path: pathName(node) }));
  const rows = references.map(({ entry, node }) =>
    [[...entry.directories.map((part) => (part.prefix || '') + part.name), entry.fileName].join('/'), pathName(node)]
      .map(csvCell)
      .join(',')
  );
  return {
    files,
    archiveName,
    mappingPath: pathName(mapping),
    mappingCsv: '\ufeff' + ['"Pôvodná cesta","Cesta v ZIP"', ...rows].join('\r\n') + '\r\n',
  };
}
